// The drawer that adds a library creative to a published campaign.
//
// It replaces a `<select>` of names with the same information the library
// picker shows a draft: every candidate as a row with its artwork, format,
// approved version and readiness, the selected one previewed beside the list
// with Meta's own placement renders, and the footer saying exactly why Add is
// held when it is. The readiness sentence comes from the workspace's
// `applyBlockedReason`, so the drawer and the API refuse for the same reason.

import type { ReactNode } from 'react';
import type { Creative } from '@/api/types';
import type { CampaignSyncTarget } from '@/api/manage-types';
import type { MetaPreviewBundle } from '@/api/types.gen';
import { ResponsiveOverlay } from './responsive-overlay';
import { creativePoster, mediaLabel, posterInitials } from '@/lib/creative-slot-view';
import { useCreativeAtRevision } from './use-creative-at-revision';

export interface CreativeAddCandidate {
  creative: Creative;
  /** Why this row cannot be picked, or null when it can. */
  reason: string | null;
}

export interface CreativeAddDrawerProps {
  campaignName: string;
  provider: CampaignSyncTarget;
  candidates: CreativeAddCandidate[];
  selectedId: string;
  onSelect: (creativeId: string) => void;
  /** Absent when the user may not create creatives. */
  onCreateNew?: () => void;
  onReviewSelected: () => void;
  previews?: MetaPreviewBundle;
  /** Why Add is held, or null when the API would accept the request. */
  applyReason: string | null;
  /** The sentence shown when nothing holds Add. */
  readyLine: string;
  /** What adding does to delivery on this provider. */
  deliveryNote: string;
  applying: boolean;
  error?: string;
  onApply: () => void;
  onClose: () => void;
  /** Extra content rendered inside the overlay, e.g. a nested create flow. */
  children?: ReactNode;
}

const PROVIDER_NAME = { meta: 'Meta', ttd: 'The Trade Desk' } as const;

/** The revision an addition would add: the approved one, else the current. */
const addedRevision = (creative: Creative) => creative.approved_revision ?? creative.revision;

// Artwork and copy come from the revision that would be added, which is the
// approved one — not the library's current content, which is a newer draft
// once an edit has minted one (audit, 2026-09-18). While the archive loads
// the poster is the initials, never the draft's picture.
function Poster({ creative, size }: { creative: Creative; size: 'row' | 'pane' }) {
  const added = useCreativeAtRevision(creative, addedRevision(creative));
  const poster = creativePoster(added.creative);
  const video = creative.media_type === 'video';
  return (
    <div className={`creative-add-poster creative-add-poster--${size}${creative.channel === 'ctv' ? ' creative-add-poster--wide' : ''}`} aria-hidden="true">
      {poster ? (
        poster.kind === 'video'
          ? <video src={poster.url} muted loop playsInline className="poster-media" />
          : <img src={poster.url} alt="" loading="lazy" className="poster-media" />
      ) : (
        <div className="creative-slot-initials">{posterInitials(creative.name)}</div>
      )}
      {video && (
        <span className="creative-slot-play">
          <svg width={size === 'pane' ? 34 : 18} height={size === 'pane' ? 34 : 18} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
        </span>
      )}
      {poster && <span className="creative-slot-ratio mono">{poster.ratio}</span>}
    </div>
  );
}

function copyOf(creative: Creative): { label: string; value: string }[] {
  let spec: Record<string, unknown> = {};
  try {
    spec = JSON.parse(creative.spec) as Record<string, unknown>;
  } catch {
    spec = {};
  }
  const text = (key: string) => {
    const value = spec[key];
    return typeof value === 'string' && value.trim() ? value : null;
  };
  const rows: { label: string; value: string }[] = [];
  const headline = text('headline');
  const post = text('post_text');
  const cta = text('cta');
  if (headline) rows.push({ label: 'Headline', value: headline });
  if (post) rows.push({ label: 'Post text', value: post });
  rows.push({ label: 'Destination', value: creative.destination_url.replace(/^https?:\/\//, '') });
  if (cta) rows.push({ label: 'Call to action', value: cta.replace(/_/g, ' ').toLowerCase() });
  return rows;
}

function SelectedCopy({ creative }: { creative: Creative }) {
  const added = useCreativeAtRevision(creative, addedRevision(creative));
  if (!added.creative) {
    return (
      <div className="creative-slot-note">
        {added.pending
          ? `Loading version ${addedRevision(creative)}…`
          : `Version ${addedRevision(creative)} could not be read from the library.`}
      </div>
    );
  }
  return (
    <dl className="creative-slot-lines creative-add-copy">
      {copyOf(added.creative).map((row) => (
        <div key={row.label} className="creative-slot-line">
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CreativeAddDrawer(props: CreativeAddDrawerProps) {
  const {
    campaignName, provider, candidates, selectedId, onSelect, onCreateNew, onReviewSelected, previews,
    applyReason, readyLine, deliveryNote, applying, error, onApply, onClose, children,
  } = props;
  const selected = candidates.find((row) => row.creative.id === selectedId)?.creative;
  const pickable = candidates.filter((row) => row.reason === null).length;
  const applyHintId = 'campaign-creative-apply-why';
  const reviewed = new Set(previews?.reviewed_placements ?? []);
  return (
    <ResponsiveOverlay label="Add campaign creative" kind="form" className="drawer creative-add-drawer" onClose={onClose} dismissible={!applying}>
      <div className="drawer-hd creative-add-hd">
        <div>
          <h2>Add a creative</h2>
          <div className="creative-add-sub">
            Becomes {provider === 'meta' ? 'one more ad in each ad set' : 'one more creative on each ad group'} of {campaignName}. Existing creatives, budget and audiences do not change.
          </div>
        </div>
        <button type="button" className="btn" disabled={applying} onClick={onClose} aria-label="Close">
          Close
        </button>
      </div>
      <div className="drawer-body creative-add-body">
        <div className="creative-add-list-pane">
          <div className="creative-add-list-head">
            <span className="creative-slot-eyebrow">Creative Library</span>
            <span className="mono creative-add-count">{pickable} of {candidates.length} can be added</span>
          </div>
          <div role="listbox" aria-label="Creative Library" className="creative-add-list">
            {candidates.length === 0 && (
              <div className="creative-slot-note">No other {PROVIDER_NAME[provider]} creatives exist for this production yet.</div>
            )}
            {candidates.map(({ creative, reason }) => {
              const isSelected = creative.id === selectedId;
              const versionTag = creative.approved_revision != null ? `v${creative.approved_revision} approved` : creative.status;
              const body = (
                <>
                  <Poster creative={creative} size="row" />
                  <span className="creative-add-row-body">
                    <span className="creative-add-row-name">{creative.name}</span>
                    <span className="creative-slot-tags">
                      <span className="tag">{mediaLabel(creative)}</span>
                      {creative.format && <span className="tag">{creative.format}</span>}
                      <span className={`tag${creative.approved_revision != null ? ' tag-version' : ''}`}>{versionTag}</span>
                      {creative.approved_revision != null && creative.approved_revision !== creative.revision && (
                        <span className="tag">v{creative.revision} in draft</span>
                      )}
                    </span>
                    <span className={`creative-add-row-reason${reason ? '' : ' is-ready'}`}>
                      {reason ?? (creative.sync?.status === 'synced' ? `Synced to ${PROVIDER_NAME[provider]}` : `Version ${creative.approved_revision} approved`)}
                    </span>
                  </span>
                </>
              );
              return reason ? (
                <div key={creative.id} role="option" aria-disabled="true" aria-selected="false" data-creative-id={creative.id} className="creative-add-row is-disabled">
                  {body}
                </div>
              ) : (
                <button
                  key={creative.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  data-creative-id={creative.id}
                  className={`creative-add-row${isSelected ? ' is-selected' : ''}`}
                  disabled={applying}
                  onClick={() => onSelect(creative.id)}
                >
                  {body}
                  {isSelected && <span className="creative-add-row-check" aria-hidden="true">✓</span>}
                </button>
              );
            })}
          </div>
          {onCreateNew && (
            <button type="button" className="btn creative-add-create" onClick={onCreateNew} disabled={applying}>
              Create new creative
            </button>
          )}
        </div>

        <div className="creative-add-preview-pane">
          {selected ? (
            <>
              <div>
                <div className="creative-slot-eyebrow">Selected</div>
                <div className="creative-add-preview-name">{selected.name}</div>
                <div className="creative-slot-note">
                  {mediaLabel(selected)} · {selected.format} · version {selected.approved_revision ?? selected.revision}
                  {selected.approved_revision != null && selected.approved_revision !== selected.revision
                    ? ` is the one added; version ${selected.revision} stays a draft in the library`
                    : ''}
                </div>
              </div>
              <div className="creative-add-preview-grid">
                <Poster creative={selected} size="pane" />
                <div className="creative-add-preview-detail">
                  {provider === 'meta' && (
                    <div>
                      <div className="creative-slot-eyebrow">
                        Meta previews · {previews?.variants.length ? `${reviewed.size} of ${previews.variants.length} reviewed` : 'not generated yet'}
                      </div>
                      {previews?.variants.length ? (
                        <div className="creative-add-placements">
                          {previews.variants.map((variant) => (
                            variant.embed_url ? (
                              <a
                                key={variant.placement}
                                className={`creative-add-placement${reviewed.has(variant.placement) ? ' is-reviewed' : ''}`}
                                href={variant.embed_url}
                                target="_blank"
                                rel="noreferrer"
                                title={`Open Meta's ${variant.label} render`}
                              >
                                {variant.label}
                              </a>
                            ) : (
                              <span key={variant.placement} className="creative-add-placement is-pending">{variant.label}</span>
                            )
                          ))}
                        </div>
                      ) : (
                        <div className="creative-slot-note">Official renders appear once the creative has synced; review them from its library page.</div>
                      )}
                    </div>
                  )}
                  <SelectedCopy creative={selected} />
                  <button type="button" className="btn btn-sm" aria-label={`Review ${selected.name}`} onClick={onReviewSelected}>
                    Open in the library
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="creative-slot-note">Pick a creative to see its artwork, copy and readiness here.</div>
          )}
          {error && <p role="alert" className="creative-add-error">{error}</p>}
        </div>
      </div>
      <div className="drawer-foot creative-add-foot">
        <div className="creative-add-foot-note">
          {deliveryNote}
          <div id={applyHintId} className="field-hint" style={{ marginTop: 4 }}>{applyReason ?? readyLine}</div>
        </div>
        <div className="creative-add-foot-actions">
          <button type="button" className="btn btn-ghost" disabled={applying} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!!applyReason || applying}
            aria-describedby={applyHintId}
            onClick={onApply}
          >
            {applying ? 'Adding…' : 'Add to campaign'}
          </button>
        </div>
      </div>
      {children}
    </ResponsiveOverlay>
  );
}
